/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  adminService,
  SupportTicket,
  SupportTicketsResponse,
} from "../../services/admin/admin.service";
import {
  DataTable,
  DataTablePagination,
} from "../../components/ui/data-table";
import { User, Building2, Loader2 } from "lucide-react";
import { format } from "date-fns";

// UI Components
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const columns: ColumnDef<SupportTicket>[] = [
  {
    id: "userType",
    header: "User Type",
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        {row.original.userType === "BRAND" ? (
          <Building2 className="w-4 h-4" style={{ color: "#d7785b" }} />
        ) : (
          <User className="w-4 h-4 text-muted-foreground" />
        )}
        <span
          className={`font-medium ${
            row.original.userType === "BRAND"
              ? "text-foreground"
              : "text-muted-foreground"
          }`}
          style={row.original.userType === "BRAND" ? { color: "#d7785b" } : {}}
        >
          {row.original.userType}
        </span>
      </div>
    ),
    meta: { width: "w-36" },
  },
  {
    id: "userId",
    header: "User ID",
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground font-mono">
        {row.original.userId.length > 20
          ? `${row.original.userId.substring(0, 20)}...`
          : row.original.userId}
      </span>
    ),
    meta: { width: "w-52" },
  },
  {
    id: "description",
    header: "Description",
    cell: ({ row }) => {
      // Strip HTML tags for preview
      const textContent = row.original.description.replace(/<[^>]*>/g, "");
      return (
        <span className="text-sm text-foreground">
          {textContent.length > 50
            ? `${textContent.substring(0, 50)}...`
            : textContent}
        </span>
      );
    },
  },
  {
    id: "mediaLinks",
    header: "Attachments",
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {row.original.mediaLinks.length > 0
          ? `${row.original.mediaLinks.length} file(s)`
          : "No attachments"}
      </span>
    ),
    meta: { width: "w-36" },
  },
  {
    id: "createdAt",
    header: "Created",
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {format(new Date(row.original.createdAt), "MMM dd, yyyy")}
      </span>
    ),
    meta: { width: "w-32" },
  },
];

export default function SupportTickets() {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState<
    "all" | "BRAND" | "AFFILIATE"
  >("all");
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(
    null
  );
  const [showModal, setShowModal] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Handle pagination change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const loadTickets = useCallback(async () => {
    // Use different loading states for initial load vs pagination
    if (page === 1 && tickets.length === 0) {
      setLoading(true);
    } else {
      setPaginationLoading(true);
    }

    try {
      const response: SupportTicketsResponse =
        await adminService.getSupportTickets(page, pageSize);
      setTickets(response.data || []);
      setTotal(response.pagination?.total || 0);
      setTotalPages(response.pagination?.totalPages || 0);
    } catch (error) {
      console.error("Failed to load support tickets:", error);
      setTickets([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
      setPaginationLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Reload tickets when filter changes
  useEffect(() => {
    if (page === 1) {
      loadTickets();
    } else {
      setPage(1); // Reset to page 1 when filter changes
    }
  }, [selectedUserType, loadTickets]);

  const handleViewTicket = async (ticketId: string) => {
    try {
      const ticket = await adminService.getSupportTicketById(ticketId);
      setSelectedTicket(ticket);
      setShowModal(true);
    } catch (error) {
      console.error("Failed to load ticket details:", error);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTicket(null);
  };

  // Filter tickets locally based on user type selection
  const filteredTickets = tickets.filter((ticket) => {
    const matchesUserType =
      selectedUserType === "all" || ticket.userType === selectedUserType;
    return matchesUserType;
  });

  // Handle user type filter change
  const handleUserTypeChange = (value: "all" | "BRAND" | "AFFILIATE") => {
    setSelectedUserType(value);
    setPage(1); // Reset to first page when filter changes
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-semibold text-foreground mb-2">
            Support Tickets
          </h1>
          <p className="text-muted-foreground">
            {loading
              ? "Loading support tickets..."
              : `Manage and respond to support requests from brands and affiliates`}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground">
            {!loading && total > 0 && (
              <div className="flex items-center gap-2">
                <span
                  className="text-white px-4 py-2 rounded-full font-medium shadow-sm"
                  style={{ backgroundColor: "#d7785b" }}
                >
                  {total} total tickets
                </span>
                {selectedUserType !== "all" && (
                  <span className="bg-muted text-muted-foreground px-3 py-1 rounded-full font-medium text-xs border border-border">
                    {selectedUserType} filter
                  </span>
                )}
              </div>
            )}
          </div>
          <Select value={selectedUserType} onValueChange={handleUserTypeChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by user type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All User Types</SelectItem>
              <SelectItem value="BRAND">Brand</SelectItem>
              <SelectItem value="AFFILIATE">Affiliate</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
        {/* Show filtered count if different from total */}
        {filteredTickets.length !== tickets.length && (
          <div className="px-5 py-3 bg-muted border-b border-border">
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground">
                Showing {filteredTickets.length} of {tickets.length} tickets
                for {selectedUserType} users
              </span>
            </div>
          </div>
        )}
        <div className="px-5 py-4">
          <h3 className="text-xl font-semibold text-foreground">
            Support Tickets ({(total || 0).toLocaleString()})
          </h3>
        </div>

        <DataTable
          columns={columns}
          data={filteredTickets}
          isLoading={loading}
          onRowClick={(ticket) => handleViewTicket(ticket.id)}
          emptyText="No support tickets found."
        />

        {/* Pagination - show based on server data, not filtered data */}
        {paginationLoading ? (
          <div className="px-5 py-4 border-t border-border">
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm text-muted-foreground">
                Loading tickets...
              </span>
            </div>
          </div>
        ) : (
          totalPages > 1 && (
            <DataTablePagination
              page={page}
              totalPages={totalPages}
              total={total}
              pageSize={pageSize}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              pageSizeOptions={[10, 20, 50, 100]}
              onPageChange={handlePageChange}
              noun="tickets"
            />
          )
        )}
      </div>

      {/* Modal for ticket details */}
      {showModal && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-foreground">
                Support Ticket Details
              </h2>
              <button
                onClick={closeModal}
                className="text-muted-foreground hover:text-muted-foreground"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    Ticket ID
                  </label>
                  <p className="text-sm text-foreground font-mono bg-muted p-2 rounded">
                    {selectedTicket.id}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    User Type
                  </label>
                  <div className="flex items-center gap-2">
                    {selectedTicket.userType === "BRAND" ? (
                      <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    ) : (
                      <User className="w-4 h-4 text-green-600 dark:text-green-400" />
                    )}
                    <span
                      className={`font-medium ${
                        selectedTicket.userType === "BRAND"
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-green-600 dark:text-green-400"
                      }`}
                    >
                      {selectedTicket.userType}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    User ID
                  </label>
                  <p className="text-sm text-foreground font-mono bg-muted p-2 rounded">
                    {selectedTicket.userId}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    Created At
                  </label>
                  <p className="text-sm text-foreground">
                    {format(new Date(selectedTicket.createdAt), "PPPp")}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Description
                </label>
                <div
                  className="prose max-w-none bg-muted p-4 rounded border"
                  dangerouslySetInnerHTML={{
                    __html: selectedTicket.description,
                  }}
                />
              </div>

              {selectedTicket.mediaLinks.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Attachments ({selectedTicket.mediaLinks.length})
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {selectedTicket.mediaLinks.map((link, index) => (
                      <div
                        key={index}
                        className="border rounded-lg overflow-hidden"
                      >
                        <img
                          src={link}
                          alt={`Attachment ${index + 1}`}
                          className="w-full h-32 object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                        <div className="p-2">
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 break-all"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-muted text-muted-foreground rounded hover:bg-muted transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
